import { Badge, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";

export interface WizardSideNavStep<K extends string> {
  key: K;
  icon: IconType;
  // Steps not yet implemented render disabled with a "Coming soon" badge.
  live?: boolean;
}

interface Props<K extends string> {
  steps: ReadonlyArray<WizardSideNavStep<K>>;
  currentStep: K;
  navigateToStep: (step: K) => void;
  stepLabel: (step: K) => string;
}

// Shared between the bid wizard and the participation-request wizard — one place for the
// step-list look and interaction.
export const WizardSideNav = <K extends string>({ steps, currentStep, navigateToStep, stepLabel }: Props<K>) => {
  const { t } = useTranslation();

  return (
    <Stack spacing="1">
      {steps.map(({ key, icon, live = true }, index) => {
        const isCurrent = key === currentStep;
        return (
          <Flex
            key={key}
            align="center"
            gap="3"
            px="3"
            py="2"
            borderRadius="md"
            cursor={live ? "pointer" : "not-allowed"}
            opacity={live ? 1 : 0.5}
            bg={isCurrent ? "smPrimarySofter" : undefined}
            color={isCurrent ? "smPrimary" : "smText"}
            _hover={live && !isCurrent ? { bg: "smBackgroundTertiary" } : undefined}
            onClick={() => live && navigateToStep(key)}
          >
            <Flex
              align="center"
              justify="center"
              boxSize="7"
              borderRadius="full"
              bg={isCurrent ? "smPrimary" : "smBackgroundTertiary"}
              color={isCurrent ? "white" : "smMuted"}
              flexShrink={0}
            >
              <Icon as={icon} boxSize="3.5" />
            </Flex>
            <Stack spacing="0.5" flex="1" minW="0">
              <Text fontSize="sm" fontWeight={isCurrent ? "semibold" : "medium"}>
                {`${index + 1}. ${stepLabel(key)}`}
              </Text>
              {!live && (
                <Badge alignSelf="flex-start" colorScheme="gray" fontSize="2xs">
                  {t("Coming soon")}
                </Badge>
              )}
            </Stack>
          </Flex>
        );
      })}
    </Stack>
  );
};
